import React, { useEffect, useRef, useState } from 'react';

export type ToastKind = 'win' | 'loss' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  detail?: string;
  /** Given extra weight — used for a personal best. */
  emphasis?: boolean;
  /** Stays until dismissed rather than fading after a few seconds. */
  sticky?: boolean;
  /** Overrides how long a non-sticky toast lingers. */
  durationMs?: number;
  action?: { label: string; onClick: () => void };
  /**
   * Identifies a toast across updates. Pushing a new toast with a tag another
   * one already has replaces it in place, which is how a "Cleared" notice
   * becomes "Rank #4" when the server finally answers, instead of stacking a
   * second card on top of the first.
   */
  tag?: string;
}

/** How long an ordinary toast stays up before fading on its own. */
const DEFAULT_DURATION_MS = 6_000;

/** Matches `toast-out` in styles.css, as a floor for the unmount fallback. */
const LEAVE_MS = 350;

function Icon({ kind }: { kind: ToastKind }): React.ReactElement {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (kind === 'win') {
    return (
      <svg {...common}>
        <path d="M4 12.5l5.5 5.5L20 7" />
      </svg>
    );
  }
  if (kind === 'loss') {
    return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 7v6M12 17h.01" />
      <circle cx="12" cy="12" r="9" strokeWidth="1.6" />
    </svg>
  );
}

type ToastItem = Toast & { leaving?: boolean };

/**
 * `toasts` is the parent's source of truth (it decides when one gets added or
 * replaced) — but removing a DOM node the instant a toast drops out of that
 * array skips the exit animation entirely, same failure shape the drawer used
 * to have. This keeps its own mirror, one render behind: an id that
 * disappears from `toasts` gets marked `leaving` here instead of being
 * removed immediately, and only actually drops out once its `toast-out`
 * animation finishes (see `onAnimationEnd` below) — or, failing that, once a
 * fixed fallback timer fires. `[data-perf='low']` forces every animation's
 * duration down to near-zero, and some browsers never fire `animationend` for
 * one that short; without the fallback a leaving toast would stay in the DOM
 * forever.
 */
function useLeavingToasts(toasts: Toast[]): [ToastItem[], (id: number) => void] {
  const [items, setItems] = useState<ToastItem[]>(toasts);
  const scheduled = useRef<Map<number, number>>(new Map());
  // Lets the effect below see what is on screen without having to depend on
  // it — depending on `items` would make this effect re-run on its own output.
  const itemsRef = useRef<ToastItem[]>(items);
  itemsRef.current = items;

  const remove = (id: number): void => {
    const handle = scheduled.current.get(id);
    if (handle !== undefined) window.clearTimeout(handle);
    scheduled.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  };

  useEffect(() => {
    const nextIds = new Set(toasts.map((toast) => toast.id));

    // Arm the unmount fallback out here, never inside the updater below: a
    // state updater is a pure function React may call more than once (it
    // does, under StrictMode), and scheduling from inside one leaves
    // orphaned timers behind.
    for (const item of itemsRef.current) {
      if (nextIds.has(item.id) || scheduled.current.has(item.id)) continue;
      scheduled.current.set(
        item.id,
        window.setTimeout(() => remove(item.id), LEAVE_MS),
      );
    }

    setItems((current) => {
      const currentIds = new Set(current.map((item) => item.id));
      const updated = current.map((item) =>
        nextIds.has(item.id) || item.leaving ? item : { ...item, leaving: true },
      );
      const added = toasts.filter((toast) => !currentIds.has(toast.id));
      return [...updated, ...added];
    });
    // `remove` only closes over refs and setState, so it is stable in
    // behaviour; leaving it out keeps this from re-running every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts]);

  useEffect(
    () => () => {
      for (const handle of scheduled.current.values()) window.clearTimeout(handle);
      scheduled.current.clear();
    },
    [],
  );

  return [items, remove];
}

/**
 * Counts each live toast down to its own dismissal, pausing while the stack
 * is hovered or focused.
 *
 * The countdown lives here rather than at the push site so it can be paused:
 * a toast that fades out from under the cursor while you are reading it — or
 * worse, while you are reaching for its button — is the single most annoying
 * thing a notification can do.
 */
function useAutoDismiss(toasts: Toast[], paused: boolean, onDismiss: (id: number) => void): void {
  const remaining = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    // Forget anything no longer on screen, so the map cannot grow forever.
    const live = new Set(toasts.map((toast) => toast.id));
    for (const id of [...remaining.current.keys()]) {
      if (!live.has(id)) remaining.current.delete(id);
    }

    if (paused) return;

    const counting = toasts.filter((toast) => !toast.sticky);
    const startedAt = Date.now();
    const handles = counting.map((toast) => {
      const left = remaining.current.get(toast.id) ?? toast.durationMs ?? DEFAULT_DURATION_MS;
      remaining.current.set(toast.id, left);
      return window.setTimeout(() => {
        remaining.current.delete(toast.id);
        onDismiss(toast.id);
      }, left);
    });

    return () => {
      // Whatever cut this short — a pause, a new toast, an unmount — banks
      // the time already served so the countdown resumes rather than restarts.
      const served = Date.now() - startedAt;
      for (const handle of handles) window.clearTimeout(handle);
      for (const toast of counting) {
        const left = remaining.current.get(toast.id);
        if (left !== undefined) remaining.current.set(toast.id, Math.max(0, left - served));
      }
    };
  }, [toasts, paused, onDismiss]);
}

/** Transient messages, so nothing has to live permanently under the board. */
export function Toasts({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}): React.ReactElement {
  const [items, remove] = useLeavingToasts(toasts);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  useAutoDismiss(toasts, hovered || focused, onDismiss);

  return (
    <div
      className="toasts"
      role="status"
      aria-live="polite"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={() => setFocused(false)}
    >
      {items.map((toast) => (
        <div
          key={toast.id}
          className={`toast is-${toast.kind}${toast.emphasis ? ' is-emphasis' : ''}${toast.leaving ? ' leaving' : ''}`}
          onAnimationEnd={() => {
            if (toast.leaving) remove(toast.id);
          }}
        >
          <span className="toast-icon">
            <Icon kind={toast.kind} />
          </span>
          <span className="toast-text">
            <strong>{toast.title}</strong>
            {toast.detail ? <span className="toast-detail">{toast.detail}</span> : null}
          </span>
          {toast.action ? (
            <button type="button" className="toast-action" onClick={toast.action.onClick}>
              {toast.action.label}
            </button>
          ) : null}
          {/* Every toast closes, not just the sticky ones. The old reasoning
              was that an auto-dismissing toast clears itself so a button on
              top of it is clutter — but "it will go away in six seconds" is
              not the same as "I am done with it now", and the one that
              matters is the one in the way. Hovering the stack also pauses
              the countdown, so the button is always still there to hit. */}
          <button
            type="button"
            className="toast-dismiss"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss notification"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
